import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { 
  Add as AddIcon, 
  Check as CheckIcon, 
  Close as CloseIcon, 
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { usePermissions } from '../hooks/usePermissions';
import { useTheme, useMediaQuery } from '@mui/material';
import LocationPicker from '../components/LocationPicker';

const Reporting: React.FC = () => {
  const { user, session } = useAuth();
  const { hasPermission, isAgentTerrain } = usePermissions();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [reports, setReports] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [formData, setFormData] = useState({
    project_id: '',
    title: '',
    description: '',
    location: '',
    latitude: '',
    longitude: '',
    status: 'draft',
  });

  useEffect(() => {
    // Attendre que la session soit disponible avant de charger
    if (session) {
      fetchReports();
      fetchProjects();
    }
  }, [session]);

  const fetchProjects = async () => {
    try {
      const response = await api.projects.getAll();
      setProjects(response.projects || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des projets:', error);
    }
  };

  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await api.reports.getAll();
      setReports(response.reports || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des rapports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReport = async () => {
    try {
      // Pour les agents, seulement projet et localisation sont requis
      if (isAgentTerrain) {
        if (!formData.project_id || !formData.latitude || !formData.longitude) {
          alert('Le projet et la localisation sont requis');
          return;
        }
        // Générer un titre automatique pour les agents
        const selectedProject = projects.find(p => p.id === formData.project_id);
        const projectTitle = selectedProject?.title || 'Projet';
        const dateStr = new Date().toLocaleDateString('fr-FR');
        const autoTitle = `Rapport ${projectTitle} - ${dateStr}`;
        
        await api.reports.create({
          project_id: formData.project_id,
          title: autoTitle,
          description: formData.description || `Rapport d'activité du ${dateStr}`,
          location: formData.location || undefined,
          latitude: parseFloat(formData.latitude),
          longitude: parseFloat(formData.longitude),
          status: 'submitted', // Les agents soumettent directement
        });
      } else {
        // Pour les autres rôles, titre et projet sont requis
        if (!formData.title || !formData.project_id) {
          alert('Le titre et le projet sont requis');
          return;
        }

        await api.reports.create({
          project_id: formData.project_id,
          title: formData.title,
          description: formData.description || undefined,
          location: formData.location || undefined,
          latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
          longitude: formData.longitude ? parseFloat(formData.longitude) : undefined,
          status: formData.status || 'draft',
        });
      }

      setOpenDialog(false);
      setFormData({
        project_id: '',
        title: '',
        description: '',
        location: '',
        latitude: '',
        longitude: '',
        status: 'draft',
      });
      fetchReports();
    } catch (error: any) {
      console.error('Erreur lors de la création du rapport:', error);
      alert('Erreur lors de la création du rapport: ' + (error.message || 'Erreur inconnue'));
    }
  };

  const handleApprove = async (reportId: string, action: 'approve' | 'reject') => {
    try {
      await api.reports.update(reportId, {
        status: action === 'approve' ? 'approved' : 'rejected',
      });
      fetchReports();
      if (selectedReport && selectedReport.id === reportId) {
        // Mettre à jour le rapport sélectionné
        const updatedReport = await api.reports.getById(reportId);
        setSelectedReport(updatedReport.report);
      }
    } catch (error: any) {
      console.error('Erreur lors de l\'approbation:', error);
      alert('Erreur lors de l\'approbation: ' + (error.message || 'Erreur inconnue'));
    }
  };

  const handleViewReport = async (reportId: string) => {
    try {
      const response = await api.reports.getById(reportId);
      setSelectedReport(response.report);
      setOpenViewDialog(true);
    } catch (error: any) {
      console.error('Erreur lors de la récupération du rapport:', error);
      alert('Erreur lors de la récupération du rapport: ' + (error.message || 'Erreur inconnue'));
    }
  };

  const handleDownloadReport = (report: any) => {
    // Générer un document texte du rapport
    const reportContent = `
RAPPORT D'ACTIVITÉ TERRAIN
===========================

Titre: ${report.title}
Projet: ${report.project_title || '-'}
Créé par: ${report.created_by_name || '-'}
Date de création: ${new Date(report.created_at).toLocaleString('fr-FR')}
Statut: ${getStatusLabel(report.status)}

Description:
${report.description || 'Aucune description'}

Localisation: ${report.location || 'Non spécifiée'}
Coordonnées GPS: ${report.latitude && report.longitude ? `${report.latitude}, ${report.longitude}` : 'Non spécifiées'}

${report.submitted_at ? `Soumis le: ${new Date(report.submitted_at).toLocaleString('fr-FR')}` : ''}
${report.approved_by_name ? `Approuvé par: ${report.approved_by_name} le ${report.approved_at ? new Date(report.approved_at).toLocaleString('fr-FR') : '-'}` : ''}
${report.approved_at ? `Date d'approbation: ${new Date(report.approved_at).toLocaleString('fr-FR')}` : ''}

${report.attachments && report.attachments.length > 0 ? `\nPièces jointes (${report.attachments.length}):\n${report.attachments.map((att: any, idx: number) => `${idx + 1}. ${att.filename || att.filepath || 'Fichier'}`).join('\n')}` : ''}
    `.trim();

    const blob = new Blob([reportContent], { type: 'text/plain;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `rapport_${report.id}_${new Date().toISOString().split('T')[0]}.txt`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDeleteReport = async (reportId: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce rapport ?')) {
      return;
    }

    try {
      await api.reports.delete(reportId);
      fetchReports();
    } catch (error: any) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression: ' + (error.message || 'Erreur inconnue'));
    }
  };

  const getStatusLabel = (status: string): string => {
    const statusMap: Record<string, string> = {
      'draft': 'Brouillon',
      'submitted': 'Soumis',
      'approved': 'Approuvé',
      'rejected': 'Rejeté',
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'submitted':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 2, sm: 0 }}
        mb={3}
      >
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
          Reporting Terrain
        </Typography>
        {hasPermission('reports:create') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
            fullWidth={isMobile}
            size={isMobile ? 'small' : 'medium'}
          >
            Nouveau rapport
          </Button>
        )}
      </Box>

      <TableContainer 
        component={Paper}
        sx={{
          overflowX: 'auto',
          '& .MuiTable-root': {
            minWidth: 650,
          }
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Titre</TableCell>
              <TableCell>Projet</TableCell>
              <TableCell>Localisation</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Créé par</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reports.length === 0 ? (
              <TableRow>
                {/* @ts-ignore - colSpan is valid HTML attribute */}
                <TableCell colSpan={7} align="center">
                  Aucun rapport pour le moment
                </TableCell>
              </TableRow>
            ) : (
              reports.map((report) => (
                <TableRow key={report.id}>
                  <TableCell>{report.title}</TableCell>
                  <TableCell>{report.project_title || '-'}</TableCell>
                  <TableCell>{report.location || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(report.status)}
                      size="small"
                      color={getStatusColor(report.status) as any}
                    />
                  </TableCell>
                  <TableCell>{report.created_by_name}</TableCell>
                  <TableCell>
                    {new Date(report.created_at).toLocaleDateString('fr-FR')}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {/* Admin : Voir et télécharger les rapports */}
                      {hasPermission('reports:view_all') && (
                        <>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewReport(report.id)}
                            title="Voir les détails"
                          >
                            <VisibilityIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleDownloadReport(report)}
                            title="Télécharger"
                          >
                            <DownloadIcon />
                          </IconButton>
                        </>
                      )}
                      {/* Agence : Pré-valider les rapports soumis */}
                      {report.status === 'submitted' && hasPermission('reports:pre_validate') && (
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => handleApprove(report.id, 'approve')}
                          title="Pré-valider"
                        >
                          <CheckIcon />
                        </IconButton>
                      )}
                      {/* SOBEBRA : Approuver/Rejeter les rapports pré-validés */}
                      {report.status === 'pre_validated' && hasPermission('reports:approve') && (
                        <>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleApprove(report.id, 'approve')}
                            title="Approuver"
                          >
                            <CheckIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleApprove(report.id, 'reject')}
                            title="Rejeter"
                          >
                            <CloseIcon />
                          </IconButton>
                        </>
                      )}
                      {/* Supprimer le rapport */}
                      {hasPermission('reports:delete') && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteReport(report.id)}
                          title="Supprimer"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Créer un rapport terrain</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Projet</InputLabel>
            <Select
              label="Projet"
              value={formData.project_id}
              onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
            >
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {!isAgentTerrain && (
            <>
              <TextField
                fullWidth
                label="Titre"
                margin="normal"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
              <TextField
                fullWidth
                label="Description"
                margin="normal"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </>
          )}
          
          {isAgentTerrain && (
            <TextField
              fullWidth
              label="Description (optionnel)"
              margin="normal"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              helperText="Le titre sera généré automatiquement à partir du projet et de la date"
            />
          )}
          
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Localisation {isAgentTerrain && <span style={{ color: 'red' }}>*</span>}
            </Typography>
            <LocationPicker
              latitude={formData.latitude ? parseFloat(formData.latitude) : null}
              longitude={formData.longitude ? parseFloat(formData.longitude) : null}
              location={formData.location}
              onLocationChange={(lat, lng, address) => {
                setFormData({
                  ...formData,
                  latitude: lat?.toString() || '',
                  longitude: lng?.toString() || '',
                  location: address || '',
                });
              }}
              height="300px"
            />
          </Box>
          
          {!isAgentTerrain && (
            <TextField
              fullWidth
              select
              label="Statut"
              margin="normal"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            >
              <MenuItem value="draft">Brouillon</MenuItem>
              <MenuItem value="submitted">Soumis</MenuItem>
            </TextField>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Annuler</Button>
          <Button 
            onClick={handleCreateReport} 
            variant="contained"
            disabled={
              isAgentTerrain 
                ? (!formData.project_id || !formData.latitude || !formData.longitude)
                : (!formData.title || !formData.project_id)
            }
          >
            {isAgentTerrain ? 'Soumettre' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour voir les détails d'un rapport */}
      <Dialog 
        open={openViewDialog} 
        onClose={() => {
          setOpenViewDialog(false);
          setSelectedReport(null);
        }} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Détails du rapport
          {selectedReport && (
            <Chip
              label={getStatusLabel(selectedReport.status)}
              size="small"
              color={getStatusColor(selectedReport.status) as any}
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {selectedReport ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Titre
              </Typography>
              <Typography variant="body1" paragraph>
                {selectedReport.title}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Projet
              </Typography>
              <Typography variant="body1" paragraph>
                {selectedReport.project_title || '-'}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Description
              </Typography>
              <Typography variant="body1" paragraph>
                {selectedReport.description || 'Aucune description'}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Localisation
              </Typography>
              <Typography variant="body1" paragraph>
                {selectedReport.location || 'Non spécifiée'}
              </Typography>

              {selectedReport.latitude && selectedReport.longitude && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Coordonnées GPS
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {selectedReport.latitude}, {selectedReport.longitude}
                  </Typography>
                </>
              )}

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Créé par
              </Typography>
              <Typography variant="body1" paragraph>
                {selectedReport.created_by_name || '-'}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Date de création
              </Typography>
              <Typography variant="body1" paragraph>
                {new Date(selectedReport.created_at).toLocaleString('fr-FR')}
              </Typography>

              {selectedReport.submitted_at && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Date de soumission
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {new Date(selectedReport.submitted_at).toLocaleString('fr-FR')}
                  </Typography>
                </>
              )}

              {selectedReport.approved_by_name && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Approuvé par
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {selectedReport.approved_by_name}
                  </Typography>
                </>
              )}

              {selectedReport.approved_at && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Date d'approbation
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {new Date(selectedReport.approved_at).toLocaleString('fr-FR')}
                  </Typography>
                </>
              )}

              {selectedReport.attachments && selectedReport.attachments.length > 0 && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Pièces jointes ({selectedReport.attachments.length})
                  </Typography>
                  {selectedReport.attachments.map((attachment: any, index: number) => (
                    <Typography key={index} variant="body2" paragraph>
                      • {attachment.filename || attachment.filepath || 'Fichier'}
                    </Typography>
                  ))}
                </>
              )}
            </Box>
          ) : (
            <Typography>Aucun rapport sélectionné</Typography>
          )}
        </DialogContent>
        <DialogActions>
          {selectedReport && hasPermission('reports:view_all') && (
            <Button
              startIcon={<DownloadIcon />}
              onClick={() => handleDownloadReport(selectedReport)}
            >
              Télécharger
            </Button>
          )}
          {selectedReport && selectedReport.status === 'submitted' && hasPermission('reports:pre_validate') && (
            <>
              <Button
                startIcon={<CheckIcon />}
                color="success"
                onClick={() => {
                  handleApprove(selectedReport.id, 'approve');
                  setOpenViewDialog(false);
                }}
              >
                Pré-valider
              </Button>
            </>
          )}
          {selectedReport && selectedReport.status === 'pre_validated' && hasPermission('reports:approve') && (
            <>
              <Button
                startIcon={<CheckIcon />}
                color="success"
                onClick={() => {
                  handleApprove(selectedReport.id, 'approve');
                  setOpenViewDialog(false);
                }}
              >
                Approuver
              </Button>
              <Button
                startIcon={<CloseIcon />}
                color="error"
                onClick={() => {
                  handleApprove(selectedReport.id, 'reject');
                  setOpenViewDialog(false);
                }}
              >
                Rejeter
              </Button>
            </>
          )}
          <Button onClick={() => {
            setOpenViewDialog(false);
            setSelectedReport(null);
          }}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Reporting;

